import React from 'react';
import { CertificateResult } from '@/types/certificate';

/**
 * Resultado de uma operação de certificado (envio, teste ou renovação).
 *
 * Separa deliberadamente "o certificado gravado" de "o que o PaymentHub está
 * usando": um envio pode dar certo e o hub continuar com o certificado antigo,
 * e essa é a situação que precisa aparecer com todas as letras. O bloco do hub
 * só existe no PIX — no certificado do extrato, quem consome é o próprio ERP, e
 * `hub_checked` não vem.
 */
export const CertificateResultCard: React.FC<{ result: CertificateResult }> = ({ result }) => (
  <div className="mb-4 space-y-2">
    <div
      className={`rounded-lg border p-3 ${
        result.success ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'
      }`}
    >
      <p className={`text-sm ${result.success ? 'text-emerald-800' : 'text-red-700'}`}>
        {result.message}
      </p>
      {result.subject_common_name && (
        <p className="text-xs text-slate-600 mt-1">
          CN do certificado: <span className="font-mono">{result.subject_common_name}</span>
        </p>
      )}
      {result.scopes && result.scopes.length > 0 && (
        <p className="text-xs text-slate-500 mt-1">{result.scopes.length} escopo(s) liberados pelo Itaú.</p>
      )}
    </div>

    {result.hub_checked && (
      <div
        className={`rounded-lg border p-3 ${
          result.hub_ok ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
        }`}
      >
        <p className={`text-sm ${result.hub_ok ? 'text-emerald-800' : 'text-amber-800'}`}>
          <span className="font-medium">PaymentHub:</span> {result.hub_message}
        </p>
        {result.hub_certificate_source && (
          <p className="text-xs text-slate-600 mt-1">
            Origem do certificado no hub: {result.hub_certificate_source === 'blob' ? 'Storage' : 'arquivo no pod'}.
          </p>
        )}
      </div>
    )}

    {result.certificate_password && (
      <div className="rounded-lg border border-slate-300 bg-slate-50 p-3">
        <p className="text-sm font-medium text-slate-800">Senha do novo certificado</p>
        <p className="font-mono text-sm text-slate-900 mt-1 break-all">{result.certificate_password}</p>
        <p className="text-xs text-slate-500 mt-1">
          Também fica gravada na configuração e continua visível no campo &ldquo;Senha do certificado&rdquo; acima.
        </p>
        {result.renewal_archive_path && (
          <p className="text-xs text-slate-500 mt-2">
            Cópia de auditoria (CSR, chave e certificado) no Storage em{' '}
            <span className="font-mono">{result.renewal_archive_path}</span>.
          </p>
        )}
        {result.correlation_id && (
          <p className="text-xs text-slate-500 mt-1">
            Protocolo Itaú: <span className="font-mono">{result.correlation_id}</span>
          </p>
        )}
      </div>
    )}
  </div>
);
